import {useMutation} from '@tanstack/react-query'
import {useAuthUser} from 'react-auth-kit'

import {useNavigate, useParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {Success, Error} from 'src/feature/bite/helpers/notification'
import useRequestParams from 'src/feature/bite/hook/request'
import {PackageListSchemaResponse} from '../domain'
import {usePackage} from '../hook'
import {API} from 'src/feature/bite/domain'
import {useIntl} from 'react-intl'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import moment from 'moment'

export function useUpdatePackage() {
  const auth = useAuthUser()
  const {packageId} = useParams()
  const {headers, errors} = useRequestParams()
  const navigate = useNavigate()

  return useMutation(
    ['PackageUpdate', packageId],
    async (values: any) => {
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Subscription/UpsertSubscriptionPackage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + auth()?.token,
        },
        body: JSON.stringify({
          ...values,
          id: packageId,
          userID: auth()?.id,
          language: headers.Language,
          timeZoneOffset,
          publishDate: moment(values.publishDate, 'DD-MM-YYYY HH:mm:ss').format(
            'YYYY-MM-DDTHH:mm:ss'
          ),
          endDate: moment(values.endDate, 'DD-MM-YYYY HH:mm:ss').format('YYYY-MM-DDTHH:mm:ss'),
        }),
      })

      const Data = await Response.json()

      if (!Response.ok || !Data.responseResult.result) {
        Error(Data?.responseResult?.exceptionDetail || errors.product.update_error)

        return
      }

      return true
    },
    {
      onSuccess: () => {
        navigate('/packages')
      },
      onError: (error) => {
        Error(errors.product.update_error)
      },
    }
  )
}

export function useCreatePackage() {
  const {headers, errors} = useRequestParams()
  const auth = useAuthUser()
  const router = useNavigate()

  const userID = auth()?.id

  return useMutation(
    ['CreateSubscriptionPackage'],
    async (values: any) => {
      const _packageItems = [...values.subscriptionItems]
      delete values.subscriptionItems
      const timeZoneOffset = getTimeZone()

      const response = await fetch(`${API}/Subscription/UpsertSubscriptionPackage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.token}`,
        },
        body: JSON.stringify({
          ...values,
          userID: auth()?.id,
          language: headers.Language,
          timeZoneOffset,
          publishDate: moment(values.publishDate, 'DD-MM-YYYY HH:mm:ss').format(
            'YYYY-MM-DDTHH:mm:ss'
          ),
          endDate: moment(values.endDate, 'DD-MM-YYYY HH:mm:ss').format('YYYY-MM-DDTHH:mm:ss'),
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.responseResult.result) {
        throw new Error(data?.responseResult?.exceptionDetail || errors.product.create_error)
      }

      await Promise.all(
        _packageItems.map(async (item) => {
          await fetch(`${API}/Subscription/UpsertSubscriptionPackageItem`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth()?.token}`,
            },
            body: JSON.stringify({
              ...item,
              subscriptionPackageID: data.response.subscriptionPackageID,
              userID: userID,
              status: 1,
              language: headers.Language,
            }),
          })
        })
      )

      return data
    },
    {
      onSuccess: () => {
        // Handle success (e.g., show a success message, redirect, etc.)
        window.location.href = '/packages'
      },
      onError: (error: Error) => {
        // Handle error (e.g., show an error message)
        console.error('Error upserting membership:', error)
      },
    }
  )
}

export function useCopyPackage() {
  const {headers, errors, signOut} = useRequestParams()
  const auth = useAuthUser()
  const {listSet, listFilter} = usePackage()

  const userID = auth()?.id

  return useMutation(
    ['CopyPackage'],
    async (id: any) => {
      const timeZoneOffset = getTimeZone()

      const response = await fetch(`${API}/Subscription/CopySubscriptionPackage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.token}`,
        },
        body: JSON.stringify({
          id,
          userID: auth()?.id,
          language: headers.Language,
          timeZoneOffset,
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.responseResult.result) {
        toast.error(data?.responseResult?.exceptionDetail || errors.product.create_error)
        return false
      }

      toast.success('Membership copied successfully')

      const ResponsePackageList = await fetch(`${API}/Subscription/GetSubscriptionPackageList`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          merchantID: auth()?.merchantID,
          language: headers.Language,
          timeZoneOffset,
        }),
      })

      if (ResponsePackageList.status === 401) {
        signOut()
      }

      const DataPackageList = await ResponsePackageList.json()

      const safeParsePackageList = PackageListSchemaResponse.safeParse(DataPackageList)

      if (!safeParsePackageList.success) {
        throw new Error(safeParsePackageList.error.message)
      }

      const parsePackageList = PackageListSchemaResponse.parse(DataPackageList)

      listSet(parsePackageList.response)

      return data
    },
    {
      onSuccess: () => {},
      onError: (error: Error) => {
        // Handle error (e.g., show an error message)
      },
    }
  )
}

export function useDeletePackage(refetch: any) {
  const {headers, errors} = useRequestParams()
  const auth = useAuthUser()

  return useMutation(
    ['DeletePackage'],
    async (id: any) => {
      const response = await fetch(`${API}/Subscription/DeleteSubscriptionPackage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.token}`,
        },
        body: JSON.stringify({
          id,
          userID: auth()?.id,
          language: headers.Language,
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.responseResult.result) {
        toast.error(data?.responseResult?.exceptionDetail || errors.product.create_error)
        return false
      }

      toast.success('Membership deleted successfully')
      refetch()

      return data
    },
    {
      onError: (error: Error) => {
        // Handle error (e.g., show an error message)
      },
    }
  )
}

export function useAddPackageItem() {
  const {headers, errors} = useRequestParams()
  const auth = useAuthUser()
  const router = useNavigate()
  const userID = auth()?.id
  const subscriptionPackageID = useParams()?.packageId
  const {detail, detailSet} = usePackage()

  return useMutation(
    ['UpsertSubscriptionPackageItem'],
    async (values: any) => {
      const response = await fetch(`${API}/Subscription/UpsertSubscriptionPackageItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.token}`,
        },
        body: JSON.stringify({
          ...values,
          subscriptionPackageID: subscriptionPackageID || '',
          userID: userID,
          status: 1,
          language: headers.Language,
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.responseResult.result) {
        toast.error(data?.responseResult?.exceptionDetail || errors.product.create_error)
        return false
      }

      const ResponsePackageItems = await fetch(
        `${API}/Subscription/GetSubscriptionPackageItemList`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            filterList: [
              {operator: 1, key: 'sbscrptnPckgID', value: [subscriptionPackageID || '']},
            ],
            orderList: [],
            paging: {currentPageNumber: 1, pageSize: 100},
            userID,
            language: headers.Language,
          }),
        }
      )

      const DataPackageItems = await ResponsePackageItems.json()

      const _packageRes = {
        ...detail,
        items: DataPackageItems?.response?.dataList || [],
      }

      detailSet(_packageRes)

      return data
    },
    {
      onSuccess: () => {
        // Handle success (e.g., show a success message, redirect, etc.)
      },
      onError: (error: Error) => {
        // Handle error (e.g., show an error message)
        console.error('Error upserting membership:', error)
      },
    }
  )
}

export function useDeletePackageItem() {
  const {headers, errors} = useRequestParams()
  const auth = useAuthUser()
  const router = useNavigate()
  const {detail, detailSet} = usePackage()

  const userID = auth()?.id

  return useMutation(
    ['DeleteSubscriptionPackageItem'],
    async (itemId: any) => {
      const response = await fetch(`${API}/Subscription/DeleteSubscriptionPackageItem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.token}`,
        },
        body: JSON.stringify({
          id: itemId,
          userID: auth()?.id,
          language: headers.Language,
        }),
      })

      const data = await response.json()

      if (!response.ok || !data.responseResult.result) {
        toast.error(data?.responseResult?.exceptionDetail || errors.product.create_error)
        return false
      }

      return true
    },
    {
      onSuccess: () => {
        // Handle success (e.g., show a success message, redirect, etc.)
      },
      onError: (error: Error) => {
        // Handle error (e.g., show an error message)
        console.error('Error upserting membership:', error)
      },
    }
  )
}

export function useAddCategory() {
  const intl = useIntl()
  const {auth, headers, errors} = useRequestParams()
  const {categories, categoriesSet} = usePackage()

  const userID = auth()?.id

  return useMutation(
    ['CreateCategory'],
    async ({name}: {name: any}) => {
      if (name.length < 3) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_LENGTH_FIELD'}, {length: 3}))
        return null
      }

      if (name.length > 50) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50}))
        return null
      }

      const Response = await fetch(`${API}/Category/AddCategory`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name,
          // storeID: '996225251718',
          userID,
          language: headers.Language,
        }),
      })

      const Data = await Response.json()

      if (!Data || !Data.response.id) {
        Error(Data?.responseResult?.exceptionDetail || errors.category.create_error)
        return null
      }

      Success(errors.category.create_success)

      categoriesSet([
        ...categories,
        {
          id: Data.response.id,
          name: name,
        },
      ])

      return {
        id: Data.response.id,
        name: name,
      }
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.category.create_error)
      },
    }
  )
}

export function useAddFamily() {
  const intl = useIntl()
  const {auth, headers, errors} = useRequestParams()
  const {family, familySet} = usePackage()

  const userID = auth()?.id

  return useMutation(
    ['CreateCategory'],
    async ({name}: {name: any}) => {
      if (name.length < 3) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_LENGTH_FIELD'}, {length: 3}))
        return null
      }

      if (name.length > 50) {
        Error(intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50}))
        return null
      }

      const Response = await fetch(`${API}/StoreProductFamily/Create`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name,
          // storeID: '996225251718',
          userID,
          language: headers.Language,
        }),
      })

      const Data = await Response.json()

      if (!Data || !Data.response.id) {
        Error(Data?.responseResult?.exceptionDetail || errors.category.create_error)
        return null
      }

      Success(errors.category.create_success)

      familySet([
        ...family,
        {
          id: Data.response.id,
          name: name,
        },
      ])

      return {
        id: Data.response.id,
        name: name,
      }
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.category.create_error)
      },
    }
  )
}

export function useGetAll() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {list} = usePackage()

  const userID = auth()?.id

  const listFilter = {
    filterList: [],
    orderList: [{type: 2, key: 'CreatedOn'}],
    paging: {
      currentPageNumber: 1,
      pageSize: list.paging.totalCount,
    },
  }

  return useMutation(
    ['productsAll'],
    async () => {
      const Response = await fetch(`${API}/StoreProduct/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          // storeID: '996225251718',
          userID,
          language: headers.Language,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()
      const safeParse = PackageListSchemaResponse.safeParse(Data)
      const parse = PackageListSchemaResponse.parse(Data)

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return []
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      return parse.response.dataList
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}
